// lib/evenements-poids.js — CE QUI DÉCIDE, C'EST LE COÛT DE L'ATTENDRE.
//
// ⚠️ MIROIR OCTET-IDENTIQUE : src/lib/evenements-poids.js. Create React App
// refuse tout import hors de `src/` (ADR-021) ; un test compare les deux
// fichiers OCTET POUR OCTET. Si ce test casse, la réponse n'est pas de le
// relâcher : c'est de recopier le fichier.
//   cp lib/evenements-poids.js src/lib/evenements-poids.js
//
// ══ LA DEMANDE (Dimitri, 2026-09-15, avec sa maquette) ═════════════════════
//
// « CE QUI DÉCIDE, C'EST LE COÛT D'ATTENDRE — PAS LA NATURE. Un webhook en
// échec sur une résa qui arrive demain n'a rien à voir avec un webhook sur
// octobre. Trois choses font monter une ligne : l'échéance (arrive demain,
// avant 17h, expire) ; l'argent qui part (paiement refusé, prix bloqué,
// caution non déposée) ; quelqu'un qui attend (un voyageur devant une porte,
// un message sans réponse). Ce qui est déjà fait redescend. DEUX NIVEAUX
// suffisent : ce qui presse en carte, le reste en trait fin. PAS DE PLAFOND :
// cacher une chose importante pour garder un écran propre est une mauvaise
// affaire. »
//
// La première version de ce chantier classait par NATURE, en liste fermée.
// Elle jugeait mal, et c'est Dimitri qui l'a dit : « il remarque n'a pas la
// même importance que webhook » — or une liste par nature met le webhook
// d'octobre et celui de demain dans le même sac.
//
// ══ CE QU'ON REPREND DE SA MAQUETTE, ET CE QU'ON AJOUTE ════════════════════
//
// REPRIS TEL QUEL de `evPoids` (docs/handoff/admin-proto.html) : les motifs
// lus dans le TEXTE de la phrase (échéance, argent qui part, quelqu'un qui
// attend, déjà fait), ses huit corrections du 15/09, et le socle `N` par
// nature. Ses commentaires voyagent avec ses motifs — ce sont ses raisons.
//
// AJOUTÉ ICI, parce que l'app a ce que sa maquette n'avait pas : des SIGNAUX
// STRUCTURÉS. Une maquette n'a que du texte ; nous avons la date d'échéance,
// le montant, la famille, et la source. Lire « arrive demain » dans une
// phrase marche tant que la phrase le dit ; lire la DATE marche toujours.
//
// ⚠️ ET LES DEUX S'ADDITIONNENT, ILS NE SE REMPLACENT PAS. Un motif de texte
// qui disparaîtrait d'une phrase réécrite laisserait le signal structuré
// tenir la ligne — et l'inverse. C'est voulu : une reformulation ne doit pas
// faire redescendre un ménage sans agent.

/** Au-dessus, c'est une carte. En dessous, un trait fin. (Maquette.) */
const SEUIL_PRESSE = 50;

/**
 * Le socle par NATURE, quand rien d'autre ne parle — table `N` de la maquette,
 * ses valeurs, complétée pour les natures de l'app qu'elle ne connaissait pas.
 *
 * ⚠️ CC — DIMITRI, 15/09 : LE FORMULAIRE ET LE CONTRÔLE MONTENT AUSSI.
 *  · un formulaire manquant bloque le code d'accès : le voyageur arrivera
 *    devant une porte qu'il ne peut pas ouvrir ;
 *  · un contrôle en attente de décision garde un ménage en suspens, et
 *    l'agent ne sait pas s'il doit repasser.
 *
 * ⚠️ CC — DIMITRI, 15/09 : INCIDENT, AVIS ET PRIX SONT IMPORTANTS.
 *  · un incident : tant qu'il n'est pas qualifié, le logement en est privé
 *    et la caution du voyageur reste bloquée ;
 *  · un avis : la réponse compte pour les voyageurs SUIVANTS, et la fenêtre
 *    pour répondre se referme ;
 *  · un prix : chaque nuit vendue au mauvais tarif est perdue.
 */
const SOCLE_NATURE = {
  // ── Les natures de l'app, valeur de la maquette quand elle existe ──────
  menage: 55, // « ménage » : 55
  caution: 50,
  incident: 55,
  conflit: 65,
  message: 45,
  formulaire: 55,
  signalement: 45,
  controle: 55, // « contrôle » : 55
  prix: 55,
  service: 20,
  avis: 55,
  promo: 5,
  sync: 15, // « synchro » / « sync » : 15
  courriel: 5,
  reservation: 15,
  remarque: 10, // « il remarque » : 10
  // ⚠️ `argent` COUVRE DEUX CHOSES CHEZ NOUS, et la maquette les séparait :
  // « paiement » (55) et « facture » (5). Notre nature est la plus large des
  // deux, donc elle prend la plus BASSE : c'est le motif « refusé / impayé »
  // (+80) qui fait remonter un paiement, jamais le socle. Une facture émise
  // ne doit pas hériter du poids d'un impayé.
  argent: 5,
  // ── Natures de l'app absentes de la maquette, posées ici ────────────────
  // Un contrat signé est un fait accompli ; une signature DEMANDÉE a son
  // propre motif de texte.
  contrat: 20,
  // Le linge et les consommables : la maquette dit 30 pour « linge ».
  stock: 20,
  // Une tâche que Dimitri s'est posée à lui-même : son échéance est la
  // sienne, et elle porte sa date (le signal structuré la relèvera).
  tache: 15,
  // Un barème de taxe à valider : une décision, pas une urgence d'heure.
  taxe: 20,
};

/**
 * LE SOCLE PAR SOURCE — ce que l'app SAIT et qu'aucune phrase ne dit.
 *
 * ⚠️ CE N'EST PLUS UN BOOLÉEN, ET C'EST TOUT LE CHANGEMENT. La version du
 * 2026-09-15 au matin tranchait ici, en liste fermée : `presse: true|false`.
 * Dimitri a renvoyé le principe : le tri ne se décide pas par source, il se
 * calcule. Cette table ne fait plus que POUSSER (+30) les sources dont l'app
 * sait, par construction, que quelqu'un attend au bout ou que l'argent part —
 * ce qu'une phrase peut ne pas dire (« Message de Pierre Martin — Cocon
 * Naturel » ne contient aucun mot d'attente, et pourtant Pierre attend).
 *
 * Un +30 ne suffit jamais à lui seul : le seuil est à 50. Il déplace, il ne
 * décide pas. Et l'inverse est vrai — une source à 0 ici peut très bien
 * passer en carte par son texte ou son échéance (`sync_beds24_muette` le fait,
 * et c'est la preuve que ce n'est plus une liste fermée).
 */
/**
 * ⚠️ 40, ET LA RAISON EST ARITHMÉTIQUE. À 30, une nature à 15 (réservation)
 * plafonnait à 45 — sous le seuil de 50. `annulation` et `demande_directe_recue`
 * partaient donc en trait fin alors que l'app SAIT que l'argent bouge et que
 * quelqu'un attend. Une poussée qui ne peut jamais faire franchir le seuil ne
 * pousse rien : elle décore.
 */
const POUSSEE_SOURCE = 40;
const SOCLE_SOURCE = {
  // ── QUELQU'UN ATTEND, OU L'ARGENT PART (+30) ───────────────────────────
  message_recu: POUSSEE_SOURCE, // un voyageur a écrit, il attend
  courriel_a_repondre: POUSSEE_SOURCE, // un humain attend une réponse
  escalade_copilote: POUSSEE_SOURCE, // le copilote a passé la main
  signalement_sejour: POUSSEE_SOURCE, // il est encore dans les murs
  demande_directe_recue: POUSSEE_SOURCE, // une vente directe attend un oui
  menage_sans_agent: POUSSEE_SOURCE, // un voyageur entrera dans du sale
  menage_desassigne: POUSSEE_SOURCE, // idem, par l'autre bout
  arrive_sans_formulaire: POUSSEE_SOURCE, // pas de pièce, pas de code
  booking_sans_caution: POUSSEE_SOURCE, // rien ne couvre une dégradation
  caution_a_restituer: POUSSEE_SOURCE, // son argent est retenu
  paiement_refuse: POUSSEE_SOURCE, // le séjour n'est pas réglé
  annulation: POUSSEE_SOURCE, // des nuits à re-vendre, au bon prix
  conflit_dates: POUSSEE_SOURCE, // deux voyageurs, une porte
  incident_a_racheter: POUSSEE_SOURCE, // le logement en est privé
  push_prix_echoue: POUSSEE_SOURCE, // on vend au mauvais tarif
  prix_ecrase_par_beds24: POUSSEE_SOURCE, // idem, écrasé par l'autre bout
  casse_signalee: POUSSEE_SOURCE, // à qualifier tant qu'on peut constater
  // ⚠️ LES PANNES DE SYNCHRO SONT DES ÉTATS, PAS DES PHRASES. La maquette les
  // reconnaissait à « muette » ou « ne partent plus » ; nos deux autres
  // formulations (« n'a jamais tourné », « a rencontré une erreur ») ne
  // disaient rien au barème, et la panne la plus grave — celle qui n'a JAMAIS
  // tourné — passait en trait fin. C'est le pire des cas selon Dimitri : « un
  // système qui paraît normal et qui ne fait plus rien ».
  sync_beds24_muette: POUSSEE_SOURCE,
  sync_beds24_erreur: POUSSEE_SOURCE,
  // Tout le reste : 0. Pas « jamais en carte » — sans poussée.
};

/**
 * ══ CE QUI EST DÉJÀ FAIT NE PRESSE JAMAIS, QUEL QUE SOIT LE TEXTE ═════════
 *
 * Le malus de −60 ne suffisait pas : « L'escalade du copilote a été résolue »
 * garde le +70 du mot « escalade » et remontait à 55 ; « Formulaire d'accueil
 * rempli » remontait par le socle de sa nature ; « Dates bloquées à la vente :
 * 3 nuits invendables » — un blocage posé par Dimitri lui-même — aussi.
 *
 * Ces sources rapportent un fait ACCOMPLI. Aucune reformulation ne doit
 * pouvoir les faire interrompre : c'est une BORNE, comme celle de l'âge, pas
 * une pondération. Liste fermée, et un test la tient source par source.
 */
const DEJA_FAIT = new Set([
  "escalade_resolue",
  "formulaire_rempli",
  "dates_bloquees",
  "dates_rouvertes",
  "menage_termine",
  "contrat_signe",
  "contrat_prestation_signe",
  "caution_deposee",
  "caution_liberee",
  "facture_partie",
  "facture_remise",
  "facture_annulee",
  "courriel_classe",
  "message_parcours_parti",
  "message_simule",
  "promo_utilise",
  "incident_rachete",
  "controle_repris",
  "cloture_journee",
  "demande_validee",
  "demande_rejetee",
  "prix_pousse",
  "paiement_direct_recu",
  "menage_a_suivi",
]);

/**
 * ⚠️ L'ARRIÉRÉ NE PRESSE PAS NON PLUS — même borne, autre raison.
 *
 * Dimitri, 2026-09-15 : « ce qui revient tous les jours sans changer, je finis
 * par ne plus le voir ». `sejours_a_constater` compte des séjours terminés
 * qu'on n'a pas encore marqués : sans échéance ni argent, ce tas se réémet à
 * l'identique chaque matin. Une carte permanente n'est plus une carte — c'est
 * du décor, et le décor rend aveugle au reste.
 */
const ARRIERE = new Set(["sejours_a_constater"]);

/**
 * LE JOUR DE CELUI QUI REGARDE — à Paris, pas à Greenwich.
 *
 * ⚠️ `new Date().toISOString().slice(0,10)` rend le jour UTC. Entre minuit et
 * deux heures du matin (heure d'été), Paris est déjà demain et UTC est encore
 * hier : « le ménage de demain » devenait « dans deux jours » et redescendait
 * de +100 à +60, au moment précis où il presse le plus.
 */
function jourLocal(maintenant) {
  const d = maintenant instanceof Date ? maintenant : new Date();
  // `en-CA` rend « AAAA-MM-JJ », le même format que nos dates de base.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Une date NUE (« 2026-09-16 ») et pas un horodatage (« …T08:00:00Z »). */
function _estDateNue(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/** Jours entiers entre deux dates ISO nues (b − a). */
function _joursEntre(a, b) {
  const ta = Date.parse(`${String(a).slice(0, 10)}T00:00:00Z`);
  const tb = Date.parse(`${String(b).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return null;
  return Math.round((tb - ta) / 86400000);
}

/**
 * L'ÉCHÉANCE STRUCTURÉE — ce que la maquette lisait dans le texte, nous le
 * lisons dans la date. `echeance` explicite d'abord, sinon un `quand` NU (les
 * « états » de `lib/operateur-evenements.js` portent une date, pas un
 * horodatage : c'est leur échéance, pas leur heure de naissance).
 *
 * ⚠️ ET LE LOINTAIN REDESCEND (−60). La consigne disait « plus loin ⇒ +10 ».
 * Elle rendait l'exemple de Dimitri impossible à tenir : notre phrase de
 * webhook contient « n'a pas pu être traité », qui vaut +80 dans SA maquette
 * — un webhook sur octobre serait donc resté en carte, exactement ce qu'il
 * refuse. Sa doctrine tranche à sa place : « ET CE QUI NE MONTE PAS : […] ce
 * qui est lointain ». Un malus, donc, et il est DIT ici plutôt que caché.
 *
 * ══ ET CE QUI EST ÉCHU DEPUIS LONGTEMPS REDESCEND AUSSI ═══════════════════
 *
 * Dimitri, 2026-09-15, devant ses cartes : « Les tâches en retard de 16, 37 et
 * 46 jours sont en carte. C'est vieux, ça ne presse plus — ça devrait être en
 * remarque. » Et sa raison, qui vaut règle : « une tâche vieille de 46 jours
 * ne presse plus. SI ELLE PRESSAIT VRAIMENT, JE L'AURAIS FAITE. »
 *
 * Une échéance qui APPROCHE monte ; une échéance DÉPASSÉE monte le temps
 * qu'on peut encore rattraper (deux jours), puis redescend — et plus elle est
 * vieille, plus elle redescend. Ce n'est pas un oubli qu'on enterre : elle
 * reste entière dans la liste « à traiter » et dans le bouton des remarques,
 * simplement elle cesse d'interrompre.
 */
function _poidsEcheance(e, jour) {
  // ⚠️ DEUX SORTES DE DATES, ET ELLES NE SE TRAITENT PAS PAREIL.
  //
  // · Un ÉTAT porte sa date comme `quand` NU : il n'existe QUE pour cette
  //   échéance (« ménage sans agent du 16/09 »). Loin dans le temps, il ne
  //   presse pas — d'où le malus.
  // · Un FAIT daté porte une `echeance` EXPLICITE en plus de son heure : la
  //   réservation est tombée ce matin, le séjour est en octobre. La date
  //   module ce qui presse, elle ne le CONDAMNE pas.
  //
  // La falaise de 120 points (de +60 à −60 entre J+3 et J+4) faisait tomber
  // trois sources d'argent sous un code promo dès qu'on leur a posé leur
  // échéance : une réservation neuve à J+8 pesait 0, une annulation −5. Une
  // correction qui rend le résultat pire que le défaut qu'elle corrige.
  const explicite = _estDateNue(e && e.echeance);
  const date = explicite
    ? e.echeance
    : _estDateNue(e && e.quand)
      ? e.quand
      : null;
  if (!date || !jour) return 0;
  const d = _joursEntre(jour, date);
  if (d === null) return 0;
  if (d >= 0) {
    // Ce qui vient : aujourd'hui ou demain passe devant tout.
    if (d <= 1) return 100;
    if (d <= 3) return 60;
    // Loin : un ÉTAT redescend toujours. Un FAIT garde son poids propre s'il
    // porte de l'argent (un séjour d'octobre est acquis), et redescend sinon
    // — c'est l'exemple fondateur de Dimitri : « un webhook en échec sur une
    // résa qui arrive demain n'a rien à voir avec un webhook sur octobre ».
    if (!explicite) return -60;
    return _argentQuiBouge(e) ? 0 : -60;
  }
  // Ce qui est passé : le retard, en jours.
  const retard = -d;
  if (retard <= 2) return 40;
  if (retard <= 7) return 0;
  if (retard <= 30) return -40;
  return -70;
}

/** Depuis combien de jours l'échéance est-elle dépassée (0 si elle ne l'est pas) ? */
function _joursDeRetard(e, jour) {
  const date = _estDateNue(e && e.echeance)
    ? e.echeance
    : _estDateNue(e && e.quand)
      ? e.quand
      : null;
  if (!date || !jour) return 0;
  const d = _joursEntre(jour, date);
  return d === null || d >= 0 ? 0 : -d;
}

/**
 * LA LIGNE TOUCHE-T-ELLE À L'ARGENT ?
 *
 * Dimitri, 2026-09-15 : « Ce qui doit rester en carte : une échéance proche,
 * ou quelque chose qui touche l'argent. Le reste descend en remarque — je le
 * retrouve dans le bouton quand je veux faire du ménage dans mes tâches. »
 */
/**
 * ══ DEUX SORTES D'ARGENT, ET UNE SEULE FAIT MONTER ═════════════════════════
 *
 * Dimitri, 2026-09-15, sur un petit-déjeuner réservé pour le 20 décembre :
 * « Non, je n'en veux pas en carte. Je ne peux RIEN FAIRE en septembre pour un
 * service réservé en décembre — c'est un constat, pas une action à faire. Le
 * montant ne devrait lever le malus de lointain que si l'argent peut BOUGER :
 * un prix qui ne part pas, un paiement refusé, une caution non déposée. Pas un
 * service déjà vendu. »
 *
 * D'où la coupure. L'ARGENT QUI BOUGE : quelque chose peut encore le faire
 * changer de main aujourd'hui — un prix qu'on repousse, un paiement qu'on
 * relance, une caution qu'on réclame, des nuits qu'on remet en vente, une
 * vente directe qui attend un oui. L'ARGENT CONSTATÉ : il est déjà passé,
 * déjà vendu, déjà facturé. Le second ne lève rien : loin dans le temps, il
 * redescend comme n'importe quel constat.
 */
const ARGENT_QUI_BOUGE = new Set([
  "push_prix_echoue",
  "prix_ecrase_par_beds24",
  "paiement_refuse",
  "booking_sans_caution",
  "caution_a_restituer",
  // Des nuits à remettre en vente, au bon prix.
  "annulation",
  // Une vente qui attend un oui — et qui part ailleurs si personne ne répond.
  "demande_directe_recue",
  // ⚠️ ELLE RESTE ICI (correction 7 de Dimitri, reprise de sa maquette) :
  // « une réservation qui arrive est une bonne nouvelle qui ENGAGE. 452 €
  // entrent, mais il faut un ménage, un formulaire, des codes. Ce n'est pas un
  // constat : c'est le début d'une chaîne. » La chaîne, elle, se prépare.
  "reservation_neuve",
]);

const ARGENT_CONSTATE = new Set([
  // Déjà vendu : rien à faire aujourd'hui pour un service de décembre.
  "service_reserve",
  "service_en_revue",
  "facture_partie",
  "facture_remise",
  "facture_annulee",
  "paiement_direct_recu",
  "caution_deposee",
  "caution_liberee",
  "demande_validee",
  "demande_rejetee",
  "demande_directe_expiree",
  "creneau_direct_perdu",
]);

/**
 * ⚠️ L'ARGENT PÉRISSABLE — celui qui est attaché à une NUIT.
 *
 * Un prix qui n'est pas parti coûte à chaque nuit vendue… tant qu'il reste des
 * nuits à vendre. Sur le 1er juin, il n'y a plus rien à pousser : l'argent ne
 * peut plus partir, il est parti. Ces deux sources ne lèvent donc pas la borne
 * d'ÂGE — sans quoi un échec de push sur une nuit révolue tient une carte pour
 * toujours (155 pour le 01/06, mesuré par la revue).
 */
const ARGENT_PERISSABLE = new Set([
  "push_prix_echoue",
  "prix_ecrase_par_beds24",
]);

/**
 * L'argent peut-il encore bouger sur cette ligne ?
 *
 * ⚠️ LA SOURCE DÉCIDE, PAS LE MONTANT. Un service vendu porte un montant, et
 * ça n'en fait pas une action. Le montant reste un POIDS (+35) ; il ne lève
 * aucune borne. Sans source connue (un objet fabriqué à la main dans un
 * test), la nature tranche — c'est le seul cas où elle sert encore.
 */
function _argentQuiBouge(ev) {
  const src = String(ev.source || "");
  if (ARGENT_QUI_BOUGE.has(src)) return true;
  if (ARGENT_CONSTATE.has(src)) return false;
  if (src) return false;
  return ["argent", "caution", "prix"].includes(String(ev.nature || ""));
}

/** Idem, pour la borne d'ÂGE : l'argent d'une nuit passée ne bouge plus. */
function _argentEncoreEnJeu(ev) {
  if (ARGENT_PERISSABLE.has(String(ev.source || ""))) return false;
  return _argentQuiBouge(ev);
}

/**
 * Le poids d'une annonce. PUR : mêmes entrées, même sortie, aucune lecture.
 *
 * @param {{phrase?:string, nature?:string, famille?:string, quand?:string,
 *   echeance?:string, montant?:number, source?:string}} e
 * @param {{jour?:string}} [opts] — « YYYY-MM-DD », le jour de celui qui
 *   regarde. Sans lui, les signaux d'échéance se taisent (ils ne se devinent
 *   pas : un poids calculé sur un faux jour serait pire que pas de signal).
 * @returns {number}
 */
function poidsEvenement(e, opts) {
  const ev = e || {};
  const jour = (opts && opts.jour) || null;
  // La maquette compose le texte jugé avec la phrase ET son étiquette de
  // nature (`(e.ph||"")+" "+(e.q||"")`). On fait pareil, avec notre nature.
  const t = `${ev.phrase || ""} ${ev.nature || ""}`
    .replace(/<[^>]+>/g, "")
    .toLowerCase();
  let p = 0;

  /* ── 1 · l'échéance ── */
  if (
    /arrive demain|arrive aujourd|dans \d ?h|avant \d{1,2} ?h|ce soir|expire (demain|aujourd|dans)/i.test(
      t,
    )
  )
    p += 100;
  else if (
    /arrive dans [123] jours?|d.ici (demain|[123] jours?)|sous [123] jours?/i.test(
      t,
    )
  )
    p += 60;
  else if (/arrive dans \d+ jours?|dans \d+ semaines?/i.test(t)) p += 10;

  /* ── 2 · l'argent qui part ── */
  if (/refusé|impayé|non déposée|pas déposée|échec de paiement/i.test(t))
    p += 80;
  /* un prix qui n'est pas parti chez les plateformes se vend au mauvais tarif */
  if (/n.est pas parti|pas parti vers|en attente d.envoi|diverge/i.test(t))
    p += 40;
  if (
    /\d[\d\s]*€/.test(t) &&
    /perd|manque|invendu|sans réservation|trou|à côté|en jeu/i.test(t)
  )
    p += 35;

  /* ── 3 · quelqu'un qui attend ── */
  if (/devant la porte|ne peut pas entrer|bloqué dehors|sur place/i.test(t))
    p += 100;
  if (/attend une réponse|sans réponse|a passé la main|escalade/i.test(t))
    p += 70;
  /* un voyageur qui demande quelque chose attend, même si le mot n'y est pas */
  if (/il demande|elle demande|demande son|demande si|a écrit|signale/i.test(t))
    p += 25;
  if (/personne n.est prévu|sans agent|personne n.est désigné/i.test(t))
    p += 75;

  /* ── ce qui est déjà fait ne presse pas ── */
  if (
    /a été (remise|envoyée|partie?|classé)|sont partis|ont été classés|numéro attribué/i.test(
      t,
    )
  )
    p -= 60;

  /* ⚠️ CC — QUATRE CORRECTIONS DE DIMITRI, 15/09.

     1 · UN VOYAGEUR QUI POSE UNE QUESTION PASSE AVANT UN LIVRET.
         Le livret n'est pas la seule source des codes — les messages du
         parcours les portent aussi. Un livret non publié à trois jours est
         rattrapable ; un voyageur sans réponse ne l'est pas.

     2 · UNE CASSE SIGNALÉE PENDANT LE SÉJOUR EST URGENTE.
         Le voyageur est encore dans les murs : c'est le seul moment où on
         peut constater, décider, et lui parler. Après son départ, il n'y a
         plus que la caution — et la discussion est perdue d'avance.

     3 · DEUX RÉSERVATIONS SUR LES MÊMES DATES SONT UNE URGENCE ABSOLUE.
         Quelqu'un arrivera devant une porte déjà occupée. Chaque heure
         d'attente rend l'annulation plus coûteuse.

     4 · UNE SYNCHRONISATION MUETTE EST UNE URGENCE SILENCIEUSE.
         Rien ne se voit à l'écran, et pourtant plus aucun prix ni blocage
         ne part vers les plateformes. C'est le pire des cas : un système
         qui paraît normal et qui ne fait plus rien. */

  /* un voyageur qui attend une réponse */
  if (
    /il demande|elle demande|demande son|demande si|a écrit|nous écrit/i.test(t)
  )
    p += 50;

  /* une casse signalée pendant le séjour — le voyageur est encore là */
  if (
    /signale/i.test(t) &&
    /encore dans les murs|jusqu.au|sur place|pendant son séjour/i.test(t)
  )
    p += 65;

  /* deux réservations sur les mêmes dates */
  if (/deux réservations|mêmes dates|double.?réservation|chevauche/i.test(t))
    p += 90;

  /* la synchronisation qui ne passe plus */
  if (
    /aucune synchronisation|ne partent plus|synchronisation.*muette|après \d+ tentatives/i.test(
      t,
    )
  )
    p += 85;

  /* ⚠️ CC — QUATRE CORRECTIONS DE PLUS, DIMITRI, 15/09.

     5 · UNE RÉSERVATION QUI N'ENTRE PAS EST UNE URGENCE.
         Un webhook en échec veut dire qu'une réservation existe chez la
         plateforme et pas chez Dimitri. Il vendra ces dates une deuxième
         fois — c'est un double-booking en préparation, silencieux.

     6 · UN PRIX QUI NE SORT PAS COÛTE CHAQUE NUIT.
         Cinq essais et les plateformes affichent encore l'ancien : chaque
         nuit vendue l'est au mauvais tarif, et Dimitri croit avoir changé
         son prix.

     7 · UNE RÉSERVATION QUI ARRIVE EST UNE BONNE NOUVELLE QUI ENGAGE.
         452 € entrent, mais il faut un ménage, un formulaire, des codes.
         Ce n'est pas un constat : c'est le début d'une chaîne.

     8 · UN SERVICE DEMANDÉ EST DE L'ARGENT QUI ATTEND.
         Un voyageur veut payer un extra. S'il n'a pas de réponse, il
         n'insiste pas — et la vente est perdue sans qu'on le sache. */

  /* une réservation qui n'est pas entrée dans le calendrier */
  if (
    /n.a pas pu être traité|pas dans votre calendrier|n.est pas dans le calendrier/i.test(
      t,
    )
  )
    p += 80;

  /* un prix qui refuse de partir, malgré les essais */
  if (/malgré \w+ essais|affichent encore l.ancien|n.est pas parti/i.test(t))
    p += 55;

  /* une réservation nouvelle : elle engage une chaîne */
  if (/une réservation du|nouvelle réservation/i.test(t)) p += 45;

  /* un service demandé et pas actif */
  if (/service/i.test(t) && /demandé|n.est actif|pas actif|attend/i.test(t))
    p += 50;

  /* ── le socle par nature, quand rien d'autre ne parle ── */
  p += SOCLE_NATURE[String(ev.nature || "").toLowerCase()] || 10;

  /* ══ LES SIGNAUX STRUCTURÉS — ce que la maquette ne pouvait pas avoir ══ */

  /* L'échéance lue dans la DATE, pas dans la phrase. */
  p += _poidsEcheance(ev, jour);

  /* Un montant en jeu est un signal d'argent, même sans mot pour le dire. */
  if (Number.isFinite(Number(ev.montant)) && Math.abs(Number(ev.montant)) > 0)
    p += 35;

  /* QUELQU'UN ATTEND AU BOUT — dit par la source, pas par la phrase. */
  if (ev.attend === true) p += 50;
  if (ev.urgence === "haute") p += 50;

  /* « RÉGLÉ » VEUT DIRE DÉJÀ FAIT. La famille d'une annonce est posée par sa
     source : `regle` = c'est arrivé, c'est fini, il n'y a rien à faire. */
  if (ev.famille === "regle") p -= 60;

  /* Ce que l'app sait de la source, et qu'aucune phrase ne dit. */
  p += SOCLE_SOURCE[String(ev.source || "")] || 0;

  /* ⚠️ ÉCHU DEPUIS PLUS DE DEUX JOURS ET SANS ARGENT EN JEU : ÇA DESCEND.
     Le malus d'âge ne suffisait pas seul — une tâche de 46 jours dont la
     phrase déclenche deux motifs de texte serait remontée en carte. La règle
     de Dimitri est une BORNE, pas une pondération : « seule une échéance
     proche ou quelque chose qui touche l'argent reste en carte ». Les deux
     premiers jours de retard sont épargnés : une tâche échue hier est encore
     une tâche d'aujourd'hui. */
  if (_joursDeRetard(ev, jour) > 2 && !_argentEncoreEnJeu(ev))
    return Math.min(p, SEUIL_PRESSE - 1);

  /* ⚠️ ET CE QUI EST DÉJÀ FAIT NE PRESSE PAS, MÊME BIEN ÉCRIT. Même borne, et
     pour la même raison : un fait accompli n'interrompt personne. */
  if (
    DEJA_FAIT.has(String(ev.source || "")) ||
    ARRIERE.has(String(ev.source || ""))
  )
    return Math.min(p, SEUIL_PRESSE - 1);

  return p;
}

/** Carte (true) ou trait fin (false) — le seuil seul décide. */
function estEnCarte(e, opts) {
  return poidsEvenement(e, opts) >= SEUIL_PRESSE;
}

/**
 * Trie pour l'affichage : le plus lourd d'abord, puis le plus récent.
 * ⚠️ Une date NUE est normalisée à minuit pour que le tri secondaire compare
 * des choses comparables (« 2026-09-16 » et « 2026-09-15T14:00:00Z » ne se
 * comparent pas en chaînes : c'est ce qui reléguait le ménage du jour).
 */
function instantDe(e) {
  const v = e && typeof e.quand === "string" ? e.quand : "";
  if (!v) return 0;
  const iso = _estDateNue(v) ? `${v.slice(0, 10)}T00:00:00Z` : v;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

module.exports = {
  SEUIL_PRESSE,
  SOCLE_NATURE,
  SOCLE_SOURCE,
  POUSSEE_SOURCE,
  DEJA_FAIT,
  ARRIERE,
  ARGENT_QUI_BOUGE,
  ARGENT_CONSTATE,
  ARGENT_PERISSABLE,
  poidsEvenement,
  estEnCarte,
  jourLocal,
  instantDe,
  _internal: {
    _poidsEcheance,
    _estDateNue,
    _joursEntre,
    _joursDeRetard,
    _argentQuiBouge,
    _argentEncoreEnJeu,
  },
};
